import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActionButton } from 'src/app/core/models/broker-admin';
import { RoleId } from 'src/app/core/common-enums/enum';
import { FormBuildContext } from '../invite-update-sidebar.models';

@Injectable({ providedIn: 'root' })
export class InviteUpdateSidebarFormService {
  private readonly fb = inject(FormBuilder);

  /**
   * Factory method — creates the correct FormGroup based on the action button.
   */
  buildForm(modalData: ActionButton, ctx: FormBuildContext): FormGroup {
    const action = modalData.actionButton;

    if (
      action === 'Send Email Invitation' ||
      action === 'Send Email Invitation Broker Admin'
    ) {
      return this.buildInvitationForm(modalData, ctx);
    }

    if (
      action === 'Assign RM' ||
      action === 'Reassign RM or Broker' ||
      action === 'Reassign RM or Advisor'
    ) {
      return this.buildAssignRmForm(modalData, ctx);
    }

    if (action === 'Assign Client' || action === 'Reassign Client') {
      return this.buildAssignClientForm(modalData, ctx);
    }

    return this.fb.group({});
  }

  private buildInvitationForm(
    modalData: ActionButton,
    ctx: FormBuildContext
  ): FormGroup {
    const form = this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: [''],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.minLength(5),
          Validators.pattern(/^\S+@\S+\.\S+$/),
          Validators.pattern('[a-zA-Z0-9._]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}')
        ]
      ],
      phoneNo: [
        '',
        [
          Validators.minLength(10),
          Validators.maxLength(12),
          Validators.required,
          Validators.pattern(/^[6-9]\d{9}$/)
        ]
      ],
      hasRMFunctionality: [ctx.hasRMboolean],
      onboardedBy: [ctx.userId],
      virtualRMId: ['', Validators.required],
      organizationId: [ctx.organizationId],
      organizationName: [ctx.organizationName],
      role: [ctx.role],
      euin: ['', [Validators.pattern('[E]+[0-9]{6}')]]
    });

    this.applyEuinValidators(form, modalData, ctx.currentRoleId);

    return form;
  }

  private applyEuinValidators(
    form: FormGroup,
    modalData: ActionButton,
    currentRoleId: number
  ): void {
    if (currentRoleId === RoleId.Advisor) {
      form.get('euin')?.clearValidators();
    } else {
      form
        .get('euin')
        ?.setValidators([
          Validators.required,
          Validators.pattern('[E]+[0-9]{6}')
        ]);
    }

    if (modalData.actionButton === 'Send Email Invitation Broker Admin') {
      form.controls['euin'].clearValidators();
      form.controls['euin'].updateValueAndValidity();
      form.controls['virtualRMId'].clearValidators();
      form.controls['virtualRMId'].updateValueAndValidity();
    }
  }

  private buildAssignRmForm(
    modalData: ActionButton,
    ctx: FormBuildContext
  ): FormGroup {
    return this.fb.group({
      clientUserId: [modalData.clientUserId],
      oldMappingUserId: [modalData.assignedRmUserId],
      newMappingUserId: ['', Validators.required],
      brokerAdminUserId: [ctx.userId],
      emailToClient: [false]
    });
  }

  private buildAssignClientForm(
    modalData: ActionButton,
    ctx: FormBuildContext
  ): FormGroup {
    return this.fb.group({
      clientUserId: ['', Validators.required],
      oldMappingUserId: [''],
      newMappingUserId: [modalData.rmUserId],
      brokerAdminUserId: [ctx.userId],
      emailToClient: [false]
    });
  }
}
